import { getBackendUrl } from "@Utils";
/* eslint no-sequences: 0 */

import { base64StringToBlob, blobToArrayBuffer } from "blob-util";
import { convertDate } from "./luxonHelper";
import axiosWrapper from "./axiosWrapper";
import { v4 as uuid } from "uuid";
import JSZip from "jszip";
import type { GetIssueViewpointDataResponse } from "@ServerTypes/issues/issues.schema";
import type { GetFileUrlResponse } from "@ServerTypes/fileUpload/fileUpload.schema";

function OBJtoXML(obj: any) {
  let xml = "";
  for (const prop in obj) {
    xml += obj[prop] instanceof Array ? "" : `<${prop}>`;
    if (obj[prop] instanceof Array) {
      for (const array in obj[prop]) {
        xml += `<${prop}>`;
        xml += OBJtoXML(new Object(obj[prop][array]));
        xml += `</${prop}>`;
      }
    } else if (typeof obj[prop] === "object") {
      xml += OBJtoXML(new Object(obj[prop]));
    } else {
      xml += obj[prop];
    }
    xml +=
      obj[prop] instanceof Array
        ? ""
        : `</${prop
            .replace(/\sIfcGuid=".*"/g, "")
            .replace(/\sGuid=".*"/g, "")
            .replace(/\sDefaultVisibility=".*"/g, "")}>`;
  }
  xml = xml.replace(/<\/?[0-9]{1,}>/g, "");
  return xml;
}

function downloadBcf(data: any, filName: any) {
  const url = window.URL || window.webkitURL;
  const link = url.createObjectURL(data);
  const aAttr = document.createElement("a");
  aAttr.setAttribute("download", filName);
  aAttr.setAttribute("href", link);
  document.body.appendChild(aAttr);
  aAttr.click();
  document.body.removeChild(aAttr);
}

export function htmlToText(html: any) {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent;
}

async function getSnapshot(src: any) {
  src = src.replace("data:image/png;base64,", "");
  const arrayBuffer = await blobToArrayBuffer(base64StringToBlob(src));
  return new Uint8Array(arrayBuffer);
}

function getViewpoint(data: any) {
  const viewpointVisibility = `Visibility DefaultVisibility="${data._components.defaultVisibility}"`;
  const viewpointExceptions = Object.keys(data._components.visibilityExceptions)
    .map((val: string) => `Component IfcGuid="${data._components.visibilityExceptions[val].ifcGuid}"`)
    .reduce((res: any, key: any) => ((res[key] = {}), res), {});

  const selectionExceptions = Object.keys(data._components.selection)
    .map((val: string) => `Component IfcGuid="${data._components.selection[val].ifcGuid}"`)
    .reduce((res: any, key: any) => ((res[key] = {}), res), {});

  let cameraType = data._orthogonalCamera;
  if (!data._orthogonalCamera) {
    cameraType = data._perspectiveCamera;
  }

  const cameraViewPoint = Object.keys(cameraType.cameraViewPoint).reduce((res: any, key: any) => {
    res[key.toUpperCase()] = cameraType.cameraViewPoint[key];
    return res;
  }, {});

  const cameraDirection = Object.keys(cameraType.cameraDirection).reduce((res: any, key: any) => {
    res[key.toUpperCase()] = cameraType.cameraDirection[key];
    return res;
  }, {});

  const cameraUpVector = Object.keys(cameraType.cameraUpVector).reduce((res: any, key: any) => {
    res[key.toUpperCase()] = cameraType.cameraUpVector[key];
    return res;
  }, {});

  const viewpoint = {
    VisualizationInfo: {
      Components: {
        ViewSetupHints: {},
        Selection: selectionExceptions,
        [viewpointVisibility]: {
          Exceptions: viewpointExceptions,
        },
        Coloring: {},
      },
      OrthogonalCamera: {
        CameraViewPoint: cameraViewPoint,
        CameraDirection: cameraDirection,
        CameraUpVector: cameraUpVector,
        ViewToWorldScale: cameraType.viewToWorldScale,
      },
    },
  };
  return OBJtoXML(viewpoint);
}

function getMarkup(issues: any, comments: any) {
  const markupJson = {
    Markup: {
      Header: {},
    },
  };

  const topicKey = `Topic Guid="${issues.id}" TopicStatus="${issues.statusInReview?.name}"`;
  markupJson.Markup[topicKey] = {
    Title: issues.title,
    CreationDate: convertDate(Number(issues.createdDate), "standardIsoFormat"),
    CreationAuthor: issues.createdByName,
    Description: htmlToText(issues.description),
    DueDate: issues.dueDate ? convertDate(Number(issues.dueDate), "standardIsoFormat") : null,
  };
  const currentIssuesComments = Object.keys(comments).reduce(
    (res: any, key: any) => ((res[key] = comments[key]), res),
    {},
  );

  Object.keys(currentIssuesComments).forEach((commentValue: string) => {
    const viewPointKey = `Viewpoint Guid="${currentIssuesComments[commentValue].snapshotKey}"`;
    const viewPointsKey = `Viewpoints Guid="${currentIssuesComments[commentValue].snapshotKey}"`;
    const commentKey = `Comment Guid="${currentIssuesComments[commentValue]._id}"`;

    markupJson.Markup[commentKey] = {
      Date: convertDate(Number(currentIssuesComments[commentValue].createdDate), "standardIsoFormat"),
      Author: currentIssuesComments[commentValue].createdBy.fullName,
      Comment: htmlToText(currentIssuesComments[commentValue].title),
      [viewPointKey]: {},
    };
    markupJson.Markup[viewPointsKey] = {
      Viewpoint: `${currentIssuesComments[commentValue].viewpointKey}.bcfv`,
      Snapshot: `${currentIssuesComments[commentValue].snapshotKey}.png`,
    };
  });
  return OBJtoXML(markupJson);
}

export default async function bcfConverter(issues: any) {
  const zip = new JSZip();
  zip.file(
    "bcf.version",
    '<?xml version="1.0" encoding="UTF-8"?><Version VersionId="2.1" xsi:noNamespaceSchemaLocation="version.xsd" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><DetailedVersion>2.1</DetailedVersion></Version>',
  );
  await Promise.all(
    Object.keys(issues).map(async (val) => {
      const issueFolder: any = zip.folder(val);
      const comments = { ...issues[val].comments };
      Object.keys(comments)?.map((commentId: any) => {
        comments[commentId].snapshotKey = uuid();
        comments[commentId].bcfDataKey = uuid();
        comments[commentId].viewpointKey = uuid();
      });
      const markup = getMarkup(issues[val], comments);
      issueFolder.file("markup.bcf", markup);

      const viewpointData = await axiosWrapper.post<GetIssueViewpointDataResponse>(
        `${getBackendUrl()}/issues/getIssueViewpointData`,
        { id: val },
      );
      if (viewpointData.data.status) {
        const issueViewpoint = getViewpoint(JSON.parse((viewpointData.data.result as any).bcfData));
        issueFolder.file("viewpoint.bcfv", issueViewpoint);

        const snapshotImage = await axiosWrapper.post<GetFileUrlResponse>(
          `${getBackendUrl()}/fileSystemServer/getFileUrl`,
          {
            key: issues[val].snapshot,
            type: "IssueImage",
          },
        );
        const snapshot = await getSnapshot(snapshotImage.data.image);
        issueFolder.file("snapshot.png", snapshot);
      }

      await Promise.all(
        Object.keys(comments).map(async (commentVal) => {
          if (comments[commentVal].bcfData) {
            const commentViewpoint = getViewpoint(JSON.parse(comments[commentVal].bcfData));
            issueFolder.file(`${comments[commentVal].viewpointKey}.bcfv`, commentViewpoint);

            const snapshotComment = await getSnapshot(comments[commentVal].snapshot);
            issueFolder.file(`${comments[commentVal].snapshotKey}.png`, snapshotComment);
          }
        }),
      );
    }),
  );

  zip
    .generateAsync({
      type: "blob",
    })
    .then((data: any) => {
      downloadBcf(data, "cube.bcfzip");
    });
}
